import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconNews } from '@tabler/icons-react';
import { ChevronDown } from 'lucide-react';
import { getBadgeLabel, getBadgeLabelPt } from './changelogUtils';
import './ChangelogSidebar.css';

function formatVersionLabel(version) {
  if (!version || typeof version !== 'string') return '';
  const trimmed = version.trim().replace(/^v/i, '');
  return trimmed ? `Versão ${trimmed}` : '';
}

function ChangelogUpdateCard({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const descRef = useRef(null);

  const badge = getBadgeLabel(entry.types);
  const title = entry.title || 'Sem título';
  const description = (entry.plaintextDetails || '').trim();

  useEffect(() => {
    setExpanded(false);
  }, [entry.id]);

  useEffect(() => {
    if (expanded || !description) {
      setCanExpand(false);
      return undefined;
    }
    const el = descRef.current;
    if (!el) return undefined;
    const check = () => {
      setCanExpand(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [description, expanded]);

  const toggleExpand = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  const handleCardClick = useCallback(() => {
    if (!description) return;
    if (canExpand || expanded) toggleExpand();
  }, [canExpand, description, expanded, toggleExpand]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick]
  );

  const interactive = Boolean(description && (canExpand || expanded));
  const showChevron = interactive;

  return (
    <article
      className={`changelog-card${expanded ? ' changelog-card--expanded' : ''}${interactive ? ' changelog-card--expandable' : ''}`}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-expanded={interactive ? expanded : undefined}
      onClick={interactive ? handleCardClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
    >
      <div className="changelog-card-top">
        {badge ? (
          <span className={`changelog-card-badge changelog-card-badge--${badge}`}>
            {getBadgeLabelPt(badge)}
          </span>
        ) : (
          <span className="changelog-card-top-spacer" aria-hidden="true" />
        )}
        {showChevron ? (
          <ChevronDown
            className={`changelog-card-chevron${expanded ? ' changelog-card-chevron--expanded' : ''}`}
            size={13}
            strokeWidth={2}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <h3 className="changelog-card-title">{title}</h3>
      {description ? (
        <p
          ref={descRef}
          className={`changelog-card-desc${expanded ? ' changelog-card-desc--expanded' : ''}`}
        >
          {description}
        </p>
      ) : null}
    </article>
  );
}

export function ChangelogSidebar({ isOpen, onClose, entries, loading, error, version }) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const versionLabel = formatVersionLabel(version);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), 380);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  return createPortal(
    <>
      <div
        className={`changelog-sidebar-backdrop${visible ? ' is-visible' : ''}`}
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        className={`changelog-sidebar${visible ? ' is-open' : ''}`}
        aria-label="Últimas atualizações"
        aria-hidden={!visible}
      >
        <div className="changelog-sidebar-body custom-scroll">
          <div className="changelog-sidebar-intro">
            <IconNews className="changelog-sidebar-intro-icon" size={17} stroke={1.75} aria-hidden="true" />
            <div className="changelog-sidebar-intro-text">
              <h2 className="changelog-sidebar-intro-title">Últimas atualizações</h2>
              {versionLabel ? (
                <p className="changelog-sidebar-intro-version">{versionLabel}</p>
              ) : null}
            </div>
          </div>

          {loading ? <div className="changelog-sidebar-status">Carregando...</div> : null}
          {error && !loading ? (
            <div className="changelog-sidebar-status changelog-sidebar-status--error">{error}</div>
          ) : null}
          {!loading && !error && entries.length === 0 ? (
            <div className="changelog-sidebar-status">Nenhuma atualização no momento.</div>
          ) : null}
          {!loading && entries.length > 0 ? (
            <div className="changelog-sidebar-list">
              {entries.map((entry, index) => (
                <ChangelogUpdateCard key={entry.id || index} entry={entry} />
              ))}
            </div>
          ) : null}
        </div>
      </aside>
    </>,
    document.body
  );
}
